import * as React from 'react';
import { useNavigate, useParams } from "react-router-dom";
import configData from "../config.json";
import { GetSession, GetToken } from '../components/Session';
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormDatePicker, FormDropDownList, FormInput, FormSwitch, FormNumericTextBox } from "../components/formComponents";
import { Button } from "@progress/kendo-react-buttons";
import { GetPermissions } from '../components/Options';
import { Upload } from '@progress/kendo-react-upload';
import '../StyleSheet.css';

const EditVouchers = () => {
    const { id } = useParams(); // editusers/id url is used to edit existing user, otherwise id is undefined
    const saveName = id === undefined ? "Create" : "Update";
    const [formkey, setFormkey] = React.useState(0); // seems to be needed to push values to form after loading
    const [permissions, setPermissions] = React.useState(null);
    const [message, setMessage] = React.useState("");
    const [uploadFile, setUploadFile] = React.useState("");
    const progressRef = React.useRef({});
    const [submitDisabled, setSubmitDisabled] = React.useState(false);
    const navigate = useNavigate();
    if (localStorage.getItem("userid") === null) navigate("/logout");
    // default layout data for page
    const [layout, setLayout] = React.useState([
        { "field": "voucherId", "label": "Voucher Id", "defaultValue": "", "isVisible": true, "type": "numeric" },
        { "field": "code", "label": "Voucher Code", "defaultValue": "", "isVisible": true, "type": "text" },
        { "field": "quantity", "label": "Quantity", "defaultValue": 1, "isVisible": true, "type": "numeric" },
        { "field": "startDate", "label": "Active From", "defaultValue": new Date(), "isVisible": true, "type": "date" },
        { "field": "endDate", "label": "Active Until", "defaultValue": new Date(), "isVisible": true, "type": "date" },
        { "field": "discountType", "label": "Discount Type", "defaultValue": { name: "Price%", value: "Price Percent Discount" }, "isVisible": true, "type": "list" },
        { "field": "discountAmount", "label": "Discount Amount", "defaultValue": 10, "isVisible": true, "type": "text" },
        { "field": "active", "label": "Active", "defaultValue": "true", "isVisible": true, "type": "boolean" },
        { "field": "totalUses", "label": "Total Uses", "defaultValue": 1, "isVisible": true, "type": "numeric" },
        { "field": "usesAllowed", "label": "Uses Allowed", "defaultValue": 1, "isVisible": true, "type": "numeric" },
        { "field": "updateAll", "label": "Update all matching Vouchers", "defaultValue": "false", "isVisible": true, "type": "boolean" }
    ]);

    const voucherTypes = [
        { name: "Weight", value: "Free Weight (t)" },
        { name: "Weight%", value: "Weight Percentage Off" },
        { name: "Price$", value: "Price Discount ($)" },
        { name: "Price%", value: "Price Percent Discount" },
        { name: "FreeUpToWeight", value: "Free if weight below limit" },
    ];

    // Gets field from layout based on field name
    const GetField = (f, def = "") => {
        let fld = layout.find(obj => { return obj.field === f });
        if (fld === undefined) return { "field": f, "label": def, "defaultValue": "", "isVisible": true, "type": "text" }
        return fld;
    }

    // State data for form. Values match form value on submit
    const [values, setValues] = React.useState({
        voucherId: GetField("voucherId")["defaultValue"],
        code: GetField("code")["defaultValue"],
        startDate: new Date(),
        endDate: new Date(),
        quantity: 1,
        discountType: GetField("discountType")["defaultValue"],
        discountAmount: 10,
        usesAllowed: GetField("usesAllowed")["defaultValue"],
        totalUses: GetField("totalUses")["defaultValue"],
        active: String(GetField("active")["defaultValue"]).toLowerCase() === "true"
    });

    // When page first loads:
    // - populate drop downs from apiv7/options
    // - if in edit mode, set initial values for all fields for apiv7/vouchers
    React.useEffect(() => {
        GetPermissions(["ViewVouchers", "NewVouchers", "EditVouchers", "EnableVouchers"], setPermissions);
        var url = configData.SERVER_URL + "apiv7/userinterfaces?$filter=viewName eq 'EditVouchers'";
        const init = {
            method: 'GET',
            accept: 'application/json',
            headers: { "Authorization": "Bearer " + GetToken() }
        }
        fetch(url, init).then(response => response.json()).then(d => {
            if (GetSession().IsMobile) {
                let data = [];
                for (let i = 0; i < d.length; i++) {
                    data.push({ field: d[i].field, label: d[i].label, type: d[i].type, format: d[i].format, isVisible: d[i].isVisible && d[i].isVisibleOnMobile });
                }
                setLayout(data);
            } else {
                setLayout(d);
            }
        });
        url = configData.SERVER_URL + "apiv7/options";
        fetch(url, init).then(response => response.json()).then(opt => {
            if (id !== undefined) {
                url = `${configData.SERVER_URL}apiv7/vouchers/${id}`;
                fetch(url, init).then(response => response.json()).then(d => {
                    var sd = d.startDate === null ? new Date() : new Date(d.startDate + "Z");
                    var ed = d.endDate === null ? new Date(sd.getFullYear() + 1, sd.getMonth(), sd.getDate()) : new Date(d.endDate + "Z");
                    var dt = voucherTypes.find(obj => { return obj.name === d.discountType });
                    setValues({
                        voucherId: d.voucherId === null ? "" : 1,
                        code: d.code === null ? "" : d.code,
                        quantity: 1,
                        startDate: sd,
                        endDate: ed,
                        discountType: dt,
                        discountAmount: d.discountAmount,
                        usesAllowed: d.usesAllowed,
                        totalUses: d.totalUses,
                        active: d.isActive === null ? "" : d.isActive,
                        updateAll: false
                    });
                    setFormkey(formkey + 1);
                })
            } 
        });
    }, []);

    // Returns value to be sent to web api:
    // If field is visible just return f
    // If field is hidden, return defaultValue from layout data
    const GetDefaultData = (f, name) => {
        let fld = GetField(name);
        if (f === undefined) f = "";
        if (fld === undefined || fld === null) return f;
        let res = fld["defaultValue"];
        if (fld["isVisible"]) res = f;
        switch (fld["type"]) {
            case "numeric":
                res = parseFloat(res);
                if (isNaN(res)) res = 0;
                break;
            case "boolean": res = (res === "true" || res === true); break;
            case "date":
                if (res === "") res = null;
                break;
            default: break;
        }
        return res;
    }

    const isNullOrEmpty = (x) => { return (x === undefined || x === null || x === ""); }

    const Validate = (dataItem) => {
        let st = "";
        if (isNullOrEmpty(dataItem.code)) st += "Code\r\n";
        if (isNullOrEmpty(dataItem.discountAmount)) st += "Discount Amount\r\n";
        if (isNullOrEmpty(dataItem.usesAllowed)) st += "Uses\r\n";
        if (st !== "") {
            alert("Please enter the following: \r\n" + st);
            return false;
        }
        return true;
    }

    // Handle submit button. For create POST the data, for edit PUT the data, then redirect to offenses index page.
    const handleSubmit = () => {
        let dataItem = { ...values };
        if (!Validate(dataItem)) return;  
        var pth = id === undefined ? "" : "/" + id;
        setSubmitDisabled(true);
        var url = `${configData.SERVER_URL}apiv7/vouchers${pth}`;
        const init = {
            method: id === undefined ? 'POST' : 'PUT',
            accept: "application/json",
            headers: {
                "Content-type": "application/json",
                "Authorization": "Bearer " + GetToken()
            },
            mode: "cors",
            cache: "no-cache",
            body: JSON.stringify({
                "voucherId": id === undefined ? 0 : id,
                "code": uploadFile !== "" ? uploadFile : GetDefaultData(dataItem.code, "code"),
                "quantity": uploadFile !== "" ? -1 : GetDefaultData(dataItem.quantity, "quantity"),
                "startDate": GetDefaultData(dataItem.startDate.toISOString(), "startDate"),
                "endDate": GetDefaultData(dataItem.endDate.toISOString(), "endDate"),
                "discountType": GetDefaultData(dataItem.discountType, "discountType").name,
                "discountAmount": GetDefaultData(dataItem.discountAmount, "discountAmount"),
                "usesAllowed": GetDefaultData(dataItem.usesAllowed, "discountAmount"),
                "barcode": "",
                "isActive": GetDefaultData(dataItem.active, "active"),
                "updateAll": id === undefined ? false :dataItem.updateAll
            })
        }
        fetch(url, init).then(response => response).then(d => {
            var msg = id === undefined ? "Voucher created" : "Voucher updated";
            if (d.status > 299) msg = `Error ${d.status} creating Voucher`;
            Redirect(msg);
        });
    }

    // navigation
    const GoBack = () => Redirect("");
    const Redirect = (msg = "") => {
        navigate("/vouchers", { state: { msg: msg } });
    }
    if (permissions && !permissions.CanView) navigate("/error401");
    if (permissions && !permissions.CanEdit && id !== undefined) navigate("/error401");
    if (permissions && !permissions.CanCreate && id === undefined) navigate("/error401");

    const onSave = (files, o, onProgress) => {
        const currentFile = files[0];
        const uid = currentFile.uid;
        progressRef.current[uid] = 0;
        files.push(currentFile);

        // Simulate save request
        return new Promise((resolve, reject) => {
            if (currentFile.validationErrors && currentFile.validationErrors.length > 0) {
                reject({ uid: uid });
            } else {
                const reader = new FileReader();
                reader.onload = () => {
                    if (reader.result && typeof reader.result === 'string') {
                        setUploadFile(reader.result);
                        setMessage("File Uploaded");
                        resolve({ uid: uid });
                    } else {
                        reject({ uid: uid });
                    }
                };
                reader.onprogress = (data) => {
                    onProgress(uid, data);
                };
                reader.onabort = () => {
                    reject({ uid: uid });
                };
                reader.onerror = () => {
                    reject({ uid: uid });
                };

                reader.readAsDataURL(currentFile.getRawFile());
            }
        });
    }

    const CodeChanged = (e) => { let v = { ...values }; v.code = e.value; setValues(v) }
    const ActiveChanged = (e) => { let v = { ...values }; v.active = e.value; setValues(v) }
    const QuantityChanged = (e) => { let v = { ...values }; v.quantity = e.value; setValues(v) }
    const StartChanged = (e) => { let v = { ...values }; v.startDate = e.value; setValues(v) }
    const EndChanged = (e) => { let v = { ...values }; v.endDate = e.value; setValues(v) }
    const DiscountTypeChanged = (e) => { let v = { ...values }; v.discountType = e.value; setValues(v) }
    const DiscountAmountChanged = (e) => { let v = { ...values }; v.discountAmount = e.value; setValues(v) }
    const UsesChanged = (e) => { let v = { ...values }; v.usesAllowed = e.value; setValues(v) }
    const UpdateAllChanged = (e) => { let v = { ...values }; v.updateAll = e.value; setValues(v) }

    // form validators
    const textValidator = (value) => !value ? "Please enter a value" : "";
    const optionValidator = (value) => value ? "" : "Please select an option";
    return (
        <>
            <div className="background-3d" />
            <div className="form-wrapper">
            <Form initialValues={values} key={formkey} render={(formRenderProps) => (
                <FormElement style={{ width: "95%" }} >
                    <fieldset className={"k-form-fieldset"}>
                        <div className="row">
                            {GetField("active")["isVisible"] && <div className='col-md-3'>
                                <Field id={"active"} name={"active"} label={GetField("active")["label"]} component={FormSwitch} disabled={permissions && !permissions.EnableVouchers} onChange={ActiveChanged} />
                            </div>}
                        </div>
                        <div className="row">
                            {GetField("code")["isVisible"] && <div className="col-md-3">
                                <Field id={"code"} name={"code"} label={GetField("code")["label"]} component={FormInput} validator={textValidator} onChange={CodeChanged} />
                            </div>}
                            {GetField("quantity")["isVisible"] && id === undefined && <div className="col-md-3">
                                <Field id={"quantity"} name={"quantity"} label={GetField("quantity")["label"]} min={1} max={100} defaultValue={1} component={FormNumericTextBox} onChange={QuantityChanged} />
                            </div>}
                            {GetField("quantity")["isVisible"] && id === undefined && <div className="col-md-3" style={{paddingTop: "40px"} }>
                                Note: If multiple barcodes are created, the voucher code will be the prefix. 
                            </div>}
                        </div>
                        <div className="row">
                            {GetField("startDate")["isVisible"] && <div className="col-md-3">
                                <Field id={"startDate"} name={"startDate"} label={GetField("startDate")["label"]} component={FormDatePicker} onChange={StartChanged}
                                    wrapperStyle={{ width: "90%", marginRight: "18px", }} />
                            </div>}
                            {GetField("endDate")["isVisible"] && <div className="col-md-3">
                                <Field id={"endDate"} name={"endDate"} label={GetField("endDate")["label"]} component={FormDatePicker} onChange={EndChanged}
                                    wrapperStyle={{ width: "90%", marginRight: "18px", }} />
                            </div>}
                        </div>
                        <div className="row">
                            {GetField("discountType")["isVisible"] && <div className="col-md-3">
                                <Field id={"discountType"} name={"discountType"} label={GetField("discountType")["label"]} component={FormDropDownList} textField="value" data={voucherTypes} validator={optionValidator} onChange={DiscountTypeChanged} />
                            </div>}
                            {GetField("code")["isVisible"] && <div className="col-md-3">
                                <Field id={"discountAmount"} name={"discountAmount"} label={GetField("discountAmount")["label"]} component={FormNumericTextBox} validator={textValidator} onChange={DiscountAmountChanged} />
                            </div>}
                            {GetField("code")["isVisible"] && <div className="col-md-6" style={{paddingTop: "10px"} }>
                                For <i>Free Weight (t)</i> this is Number of tonnes free<br />
                                For <i>Weight % off</i> this is percentage subtracted from weight<br/>
                                For <i>Price discount ($)</i> this is the number of dollars off<br />
                                For <i>Price percent discount</i> this is the discount percentage<br />
                                For <i>Free if weight below limit</i> this is the number of kg allowed for free
                            </div>}
                        </div>
                        <div className="row">
                            {GetField("usesAllowed")["isVisible"] && <div className="col-md-3">
                                <Field id={"usesAllowed"} name={"usesAllowed"} label={GetField("usesAllowed")["label"]} min={1} max={10000} component={FormNumericTextBox} validator={textValidator} onChange={UsesChanged} />
                            </div>}
                            {GetField("totalUses")["isVisible"] && <div className="col-md-3">
                                <Field id={"totalUses"} name={"totalUses"} label={GetField("totalUses")["label"]} disabled component={FormNumericTextBox} />
                            </div>}
                        </div>
                        {id && <div className="row">
                            {GetField("updateAll")["isVisible"] && <div className='col-md-3'>
                                <Field id={"updateAll"} name={"updateAll"} label={GetField("updateAll", "Update all matching Vouchers")["label"]} component={FormSwitch} onChange={UpdateAllChanged} />
                            </div>}
                        </div>}
                        {id === undefined && <div className="row" style={{ marginTop: "20px" }}>
                            <div className="col-md-6">
                                <Upload multiple={false} saveUrl={onSave} />
                            </div>
                            {message === "" && <div className='col-md-6'>Note: upload file is a text file with a voucher code on each line</div>}
                            {message && <div className='col-md-6' style={{ padding: "10px 20px", color: "blue", fontWeight: "bold" }}>{message}</div>}
                        </div>}
                    </fieldset>
                    <div className="k-form-buttons" style={{ paddingTop: 10 }}>
                        <div className='col-md-1'>
                            <Button themeColor={"primary"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={handleSubmit} disabled={submitDisabled}>{saveName}</Button>
                        </div>
                        <div className='col-md-1'>
                            <Button className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={GoBack}>Cancel</Button>
                        </div>
                    </div>
                </FormElement>
            )} />
        </div>
       </>
    );
}

export default EditVouchers